import { LibraryResponse } from 'lightning-common/lib/types/lightning-api/responses';
import { createContext, ReactNode, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useApi } from '../hooks/use-api';
import { rawLibraryDataState } from '../states/raw-library-data';
import {
    getSwrLoadingState,
    LoadingState,
} from '../utilities/swr-loading-state';

interface LibraryConfigurationContextProps {
    loadingState: LoadingState | 'not-started';
    libraryConfiguration?: LibraryResponse;
}

export const LibraryConfigurationContext =
    createContext<LibraryConfigurationContextProps>({
        loadingState: 'not-started',
    });

interface LibraryConfigurationProviderProps {
    children: ReactNode;
}

export const LibraryConfigurationProvider = ({
    children,
}: LibraryConfigurationProviderProps): JSX.Element => {
    const libraryResponse = useApi<LibraryResponse>({ path: 'api/library' });
    const [libraryData, setLibraryData] = useRecoilState(rawLibraryDataState);
    const loadingState = getSwrLoadingState(libraryResponse);

    useEffect(() => {
        if (!!libraryResponse.data && loadingState === 'loaded') {
            setLibraryData({
                libraryData: libraryResponse.data,
                loadingState,
                refresh: libraryResponse.mutate,
            });
        } else {
            setLibraryData({
                libraryData: libraryData.libraryData,
                loadingState,
                refresh: libraryResponse.mutate,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [libraryResponse.data, libraryResponse.mutate, loadingState]);

    return (
        <LibraryConfigurationContext.Provider
            value={{ loadingState, libraryConfiguration: libraryResponse.data }}
        >
            {loadingState === 'loaded' && children}
        </LibraryConfigurationContext.Provider>
    );
};
